import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Eye, EyeOff, LogIn, AlertCircle, HelpCircle } from 'lucide-react';
import { loginCliente } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    cpf: '',
    senha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatCPF = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara XXX.XXX.XXX-XX
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    
    return value;
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({
      ...prev,
      cpf: formatted
    }));
    setError('');
  };

  const handlePasswordChange = (e) => {
    setFormData(prev => ({
      ...prev,
      senha: e.target.value
    }));
    setError('');
  };

  const validateForm = () => {
    const cpfNumbers = formData.cpf.replace(/\D/g, '');
    
    if (!formData.cpf || cpfNumbers.length !== 11) {
      setError('Por favor, insira um CPF válido');
      return false;
    }
    
    if (!formData.senha || formData.senha.length < 1) {
      setError('Por favor, insira sua senha');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await loginCliente(formData.cpf, formData.senha);
      
      if (response.success) {
        console.log('Login successful, response user:', response.user);
        
        // Verifica se a senha é igual ao CPF/CNPJ (senha temporária)
        const cpfLimpo = formData.cpf.replace(/\D/g, '');
        const senhaLimpa = formData.senha.replace(/\D/g, '');
        const senhaIgualDocumento = senhaLimpa === cpfLimpo;
        
        if (senhaIgualDocumento) {
          // Senha temporária detectada - redireciona para troca de senha
          toast({
            title: "Primeiro acesso detectado",
            description: "Por segurança, você precisa alterar sua senha.",
            duration: 5000,
          });
          
          // Salva dados do usuário temporariamente para uso na tela de troca
          onLogin(response.user);
          
          navigate('/primeiro-acesso', { 
            state: { 
              userId: response.user.clienteId,
              nome: response.user.nome,
              primeiroAcesso: true
            } 
          });
        } else {
          // Senha normal - login normal
          onLogin(response.user);
          
          toast({
            title: "Login realizado com sucesso!",
            description: `Bem-vindo, ${response.user.nome}`,
            duration: 3000,
          });
          
          navigate('/');
        }
      }
    } catch (err) {
      console.error('Erro no login:', err);
      setError(err.message || 'Erro ao fazer login. Verifique seus dados.');
      
      toast({
        title: "Erro no login",
        description: err.message || 'Erro ao fazer login. Verifique seus dados.',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
            alt="Knorr Leilões"
            className="h-16 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <h1 className="text-4xl font-bold text-[rgb(46,48,146)] mb-2 hidden">
            KNORR LEILÕES
          </h1>
          <p className="text-gray-600">Faça login em sua conta</p>
        </div>

        <Card className="border-2 border-gray-200">
          <CardHeader className="text-center bg-[rgb(46,48,146)] text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-center space-x-2">
              <LogIn className="h-5 w-5" />
              <span>Entrar no Sistema</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="cpf" className="text-sm font-medium text-gray-700">
                  CPF
                </Label>
                <Input
                  id="cpf"
                  type="text"
                  placeholder="000.000.000-00"
                  value={formData.cpf}
                  onChange={handleCPFChange}
                  maxLength={14}
                  className="mt-1 border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)]"
                  disabled={loading}
                />
              </div>

              <div>
                <Label htmlFor="senha" className="text-sm font-medium text-gray-700">
                  Senha
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="senha"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua senha"
                    value={formData.senha}
                    onChange={handlePasswordChange}
                    className="border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)] pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <Alert className="border-[rgb(238,29,37)] bg-red-50">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-[rgb(238,29,37)] mt-0.5 flex-shrink-0" />
                    <AlertDescription className="text-[rgb(238,29,37)] text-sm">{error}</AlertDescription>
                  </div>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90 text-white py-3 transition-colors duration-200"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Entrando...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <LogIn className="h-4 w-4" />
                    <span>Entrar</span>
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/check-document"
                className="text-[rgb(46,48,146)] hover:text-[rgb(46,48,146)]/80 text-sm font-medium flex items-center justify-center gap-2 hover:underline"
              >
                <HelpCircle className="h-4 w-4" />
                Esqueceu sua senha?
              </Link>
              
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-600 mb-2">
                  Não tem uma conta?
                </p>
                <Link
                  to="/register"
                  className="text-[rgb(238,29,37)] hover:text-[rgb(238,29,37)]/80 text-sm font-medium hover:underline"
                >
                  Criar nova conta
                </Link>
              </div>
              
              <p className="text-sm text-gray-600 pt-2">
                Problemas para acessar? <br />
                Entre em contato conosco
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2025 Knorr Leilões. Sistema seguro e confiável.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;